import React, { useState } from 'react';
import SyncStatusIndicator from './SyncStatusIndicator';
import StatusDropdown from './StatusDropdown';
import OrderActionButtons from './OrderActionButtons';
import { usePermissionViewModel } from '../../permissions/viewmodel/permission-viewmodel';
import { updateOrderAvisCommentaire } from '../../orders-controller/order-controller';

const InlineMobileEdit = ({ value, onSave, placeholder, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try { await onSave(val); } catch {}
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <textarea
        value={val}
        autoFocus
        rows={2}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
        disabled={saving}
        className="w-full text-xs border border-emerald-400 rounded p-1 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { if (!disabled) { setVal(value || ''); setEditing(true); } }}
      className={`text-left w-full text-xs rounded px-1 py-0.5 transition-colors ${disabled ? 'cursor-default' : 'cursor-pointer hover:bg-emerald-50 border border-transparent hover:border-emerald-200'} ${val ? 'text-gray-900' : 'text-gray-400 italic'}`}
    >
      {val || placeholder}
    </button>
  );
};


const MobileOrderCard = ({
  order,
  statusInfo,
  allowedOptions,
  isSelectableStatus,
  isSelected,
  handleOrderSelection,
  openDropdown,
  handleToggleDropdown,
  dropdownPosition,
  handleStatusChange,
  formatDate,
  getClientFullName,
  handleDeleteOrder,
  newlyAddedOrders,
  getStatusInfo,
  handleCallStatusChange
}) => {
  const {hasPermission} = usePermissionViewModel('commande')
  const [localAvis, setLocalAvis] = useState(order.avisClient || '');
  const [localCommentaire, setLocalCommentaire] = useState(order.commentaire || '');

  const handleSaveAvis = async (val) => {
    await updateOrderAvisCommentaire(order.id, val, localCommentaire);
    setLocalAvis(val);
  };

  const handleSaveCommentaire = async (val) => {
    await updateOrderAvisCommentaire(order.id, localAvis, val);
    setLocalCommentaire(val);
  };

  // Get phone icon color based on call status
  const getPhoneIconColor = (callStatus) => {
    if (callStatus === null) {
      return 'text-gray-400'; // Gray for null
    } else if (callStatus === true || callStatus === 1) {
      return 'text-green-500'; // Green for successful call
    } else if (callStatus === false || callStatus === 0) {
      return 'text-red-500'; // Red for failed call
    }
    return 'text-gray-400'; // Default to gray
  };

  return (
    <div
      data-order-id={order.id}
      className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden transition-all duration-300
          ${isSelected ? "border-indigo-300 bg-indigo-50" : ""} 
          ${newlyAddedOrders[order.id] ? "highlight-new-order" : ""}`}
    >
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            {isSelectableStatus && (
              <input
                type="checkbox"
                className="h-4 w-4 mr-2 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                checked={isSelected}
                onChange={() => handleOrderSelection(order.id, order.etat)}
              />
            )}
            <div>
              <div className="text-sm font-medium text-gray-900">#{order.id}</div>
              <div className="text-xs text-gray-500">{order.reference || 'N/A'}</div>
            </div>
          </div>
          <div className="flex items-center">
            <div className="mr-2">
              <SyncStatusIndicator syncStatus={order.syncro} />
            </div>
            <span
              className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.color}`}
            >
              {statusInfo.label}
            </span>
          </div>
        </div>

        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-gray-500">Client:</span>
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-900 mr-2">{getClientFullName(order)}</span>
              <button
                onClick={() => handleCallStatusChange && handleCallStatusChange(order.id, order.call)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                title={
                  order.call === null 
                    ? "Cliquez pour marquer comme appelé" 
                    : (order.call === true || order.call === 1)
                    ? "Appel réussi - Cliquez pour marquer comme échoué" 
                    : "Appel échoué - Cliquez pour marquer comme réussi"
                }
              >
                <svg 
                  className={`h-4 w-4 ${getPhoneIconColor(order.call)}`} 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </button>
            </div>
          </div>
          {order.clientNumber && (
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-500">Téléphone:</span>
              <a
                href={`tel:${order.clientNumber}`}
                className="text-sm font-medium text-[#174540] underline flex items-center gap-1"
              >
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {order.clientNumber}
              </a>
            </div>
          )}
          <div className="flex justify-between mb-2">
            <span className="text-sm text-gray-500">Date:</span>
            <span className="text-sm text-gray-900">{formatDate(order.dateBonCommande)}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-sm text-gray-500">Total:</span>
            <span className="text-sm font-medium text-gray-900">{(order.totalTTC || 0).toFixed(2)} TND</span>
          </div>

          {order.codePromo && order.codePromo !== "0" && (
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-500">Promo:</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                {order.codePromo}
              </span>
            </div>
          )}

          {order.totalRemise > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-500">Remise:</span>
              <span className="text-sm text-green-600">
                -{order.totalRemise.toFixed(2)} TND
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <StatusDropdown
            orderId={order.id}
            currentStatus={order.etat}
            allowedOptions={allowedOptions}
            openDropdown={openDropdown}
            handleToggleDropdown={handleToggleDropdown}
            handleStatusChange={handleStatusChange}
            dropdownPosition={dropdownPosition}
            getStatusInfo={getStatusInfo}
            hasPermission={hasPermission}
          />

          <OrderActionButtons orderId={order.id} handleDeleteOrder={handleDeleteOrder} />
        </div>

        {/* Avis client & Commentaire inline edit */}
        <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-0.5">Avis client</p>
            <InlineMobileEdit
              value={localAvis}
              onSave={handleSaveAvis}
              placeholder="Cliquer pour ajouter un avis..."
              disabled={!hasPermission('write')}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-0.5">Commentaire</p>
            <InlineMobileEdit
              value={localCommentaire}
              onSave={handleSaveCommentaire}
              placeholder="Cliquer pour ajouter un commentaire..."
              disabled={!hasPermission('write')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileOrderCard;